import { VisitRequestState, VisitRequestWorkflowStep } from '@/types/graphql';
import { gql } from '@apollo/client';
import { CancelOutlined, OpenInNewRounded, RestoreFromTrash } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';

import { AxleIconButton } from '../../buttons/AxleIconButton';
import { HorizontalStack } from '../../utils/linearLayouts';
import { WarningsIcon } from '../../warnings/WarningsIcon';
import { VisitRequestActionsMenu } from '../VisitRequestActionsMenu';
import { useCancelVisitRequest } from '../mutations/cancelVisitRequest';
import { useUncancelVisitRequest } from '../mutations/uncancelVisitRequest';

export const VisitRequestActionsCellFragment = gql`
    fragment VisitRequestActionsCellFragment on VisitRequest {
        id
        state
        visitRequestWorkflowStep: workflowStep
        permissions {
            id
            edit
        }
        visit {
            id
        }
        warnings {
            message
        }
    }
`;

export interface IVisitRequestActionsCell {
    id: string;
    state: VisitRequestState;
    visitRequestWorkflowStep: VisitRequestWorkflowStep;
    permissions: {
        id: string;
        edit: boolean;
    };
    visit: {
        id: string;
    } | null;
    warnings: { message: string }[];
}
export const VisitRequestActionsCellWithMenu = ({
    visitRequest,
}: {
    visitRequest: IVisitRequestActionsCell;
}) => {
    return (
        <VisitRequestActionsMenu visitRequestId={visitRequest.id}>
            <WarningsIcon warnings={visitRequest.warnings} />
        </VisitRequestActionsMenu>
    );
};

export const VisitRequestActionsCell = ({
    visitRequest,
}: {
    visitRequest: IVisitRequestActionsCell;
}) => {
    // Hooks.
    const history = useHistory();

    // Mutations
    const { cancelVisitRequest, loading: cancelVisitRequestLoading } = useCancelVisitRequest({
        visitRequestId: visitRequest.id,
        workflowStep: visitRequest.visitRequestWorkflowStep,
    });
    const { uncancelVisitRequest, loading: uncancelVisitRequestLoading } = useUncancelVisitRequest({
        visitRequestId: visitRequest.id,
        workflowStep: visitRequest.visitRequestWorkflowStep,
    });

    const mutationLoading = cancelVisitRequestLoading || uncancelVisitRequestLoading;

    if (!visitRequest.permissions.edit) {
        return null;
    }

    return (
        <HorizontalStack>
            {visitRequest.state === VisitRequestState.PENDING && (
                <AxleIconButton
                    tooltip='Cancel'
                    icon={<CancelOutlined />}
                    disabled={mutationLoading}
                    onClick={(event) => {
                        event.stopPropagation();
                        void cancelVisitRequest();
                    }}
                />
            )}
            {visitRequest.state === VisitRequestState.CANCELED && (
                <AxleIconButton
                    tooltip='Restore'
                    icon={<RestoreFromTrash />}
                    disabled={mutationLoading}
                    onClick={(event) => {
                        event.stopPropagation();
                        void uncancelVisitRequest();
                    }}
                />
            )}
            {visitRequest.state === VisitRequestState.FULFILLED && !!visitRequest.visit?.id && (
                <AxleIconButton
                    tooltip='View Visit'
                    icon={<OpenInNewRounded />}
                    disabled={mutationLoading}
                    onClick={(event) => {
                        event.stopPropagation();
                        if (!visitRequest.visit?.id) {
                            return;
                        }
                        history.push(`/visits/${visitRequest.visit.id}`);
                    }}
                />
            )}
        </HorizontalStack>
    );
};
